import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { Trophy, Users, MapPin, Crown, Activity, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SPORT_GRADIENTS = {
  Football: 'linear-gradient(135deg, #10b981 0%, #f59e0b 100%)',
  Cricket: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  Badminton: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 100%)',
  Tennis: 'linear-gradient(135deg, #84cc16 0%, #06b6d4 100%)',
  Basketball: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
  Pickleball: 'linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)',
};
const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀', Pickleball: '🏓',
};

export default function ArenaxJoinSquad() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.get(`/arenax/squads/invite/${code}`)
      .then(r => setPreview(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Invalid invite'));
  }, [code]);

  const onAccept = async () => {
    if (!user) {
      // Send them through register/login, then come back
      navigate(`/arenax/auth/register?next=${encodeURIComponent(`/arenax/join-squad/${code}`)}`);
      return;
    }
    if (!user) {
      toast.error('You need a player account to join. Sign up as a player.');
      return;
    }
    setJoining(true);
    try {
      const r = await api.post(`/arenax/squads/invite/${code}/accept`);
      toast.success(r.data.already_member ? "You're already in this squad!" : 'Welcome to the squad!');
      navigate(`/arenax/squads/${r.data.squad_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join');
    } finally { setJoining(false); }
  };

  if (error) {
    return (
      <ArenaxLayout>
        <section className="min-h-[70vh] flex items-center justify-center px-5">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-black text-slate-100 mb-2">Invite link unavailable</h1>
            <p className="text-sm text-rose-400 mb-6">{error}</p>
            <button onClick={() => navigate('/arenax/squads')} className="px-6 py-3 rounded-xl font-bold text-slate-950 text-sm"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              Discover Other Squads
            </button>
          </div>
        </section>
      </ArenaxLayout>
    );
  }

  if (!preview) return <ArenaxLayout><div className="text-center text-slate-500 py-32">Loading invite...</div></ArenaxLayout>;

  const s = preview.squad;
  const grad = SPORT_GRADIENTS[s.primary_sport] || 'var(--ax-primary-gradient)';

  return (
    <ArenaxLayout>
      <section className="max-w-2xl mx-auto px-5 py-10" data-testid="arenax-join-squad">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 mb-4"
            style={{ background: 'rgba(245,158,11,0.1)' }}>
            <Sparkles className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-amber-300 font-bold">YOU'RE INVITED</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-100 mb-1">{s.captain_name} invited you</h1>
          <p className="text-sm text-slate-400">to join their sports squad on ArenaX</p>
        </div>

        {/* Squad identity card */}
        <div className="rounded-3xl overflow-hidden border border-cyan-500/15">
          <div className="aspect-[16/7] relative" style={{ background: grad }}>
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.22), transparent 60%)',
            }} />
            <div className="absolute -bottom-4 -right-4 text-[12rem] leading-none opacity-15 select-none pointer-events-none">
              {SPORT_ICONS[s.primary_sport] || '🏟️'}
            </div>
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <div className="text-[10px] tracking-[0.4em] uppercase text-white/85 font-bold mb-1">
                {s.primary_sport} · {s.skill_level} · {s.city}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white drop-shadow-2xl">{s.name}</h2>
              {s.bio && <p className="text-xs text-white/85 mt-2 italic max-w-xl">"{s.bio}"</p>}
            </div>
          </div>
          <div className="p-5 bg-slate-950/80 backdrop-blur">
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Stat icon={Users} label="Members" value={`${s.members_count}/${s.max_members}`} />
              <Stat icon={Activity} label="Attendance" value={`${s.attendance_rate?.toFixed(0) ?? 100}%`} color="#10b981" />
              <Stat icon={Trophy} label="Games played" value={s.games_played_total ?? 0} color="#f59e0b" />
            </div>
            <button onClick={onAccept} disabled={joining} data-testid="arenax-join-squad-accept"
              className="w-full px-4 py-3.5 rounded-xl font-bold text-slate-950 text-sm flex items-center justify-center gap-2 shadow-xl disabled:opacity-60"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                {user ? 'Accept Invite & Join Squad' : 'Sign Up & Join Squad'} <ArrowRight className="h-4 w-4" />
              </>}
            </button>
            <div className="text-[10px] text-slate-500 text-center mt-3">
              {!user && <>New to ArenaX? Get started in 60 seconds. </>}
              Free to join. No commitment beyond the next weekly game.
            </div>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Stat({ icon: Icon, label, value, color = '#06b6d4' }) {
  return (
    <div className="text-center p-3 rounded-xl bg-slate-900/60 border border-slate-800">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" style={{ color }} />
        <span className="text-[10px] tracking-wider uppercase text-slate-500 font-semibold">{label}</span>
      </div>
      <div className="text-lg font-black" style={{ color }}>{value}</div>
    </div>
  );
}
